import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { pluralize } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, CalendarDays, CalendarOff } from "lucide-react";

interface PtoBalance {
    hireDate: string | null;
    eligible: boolean;
    remainingDays: number;
    totalDays: number;
    usedDays: number;
}

interface MyAttendance {
    balance: number;
    level: number | null;
    levelAction: string | null;
    terminationAt: number;
    occurrences: Array<{
        id: string;
        occurredOn: string;
        kindLabel: string;
        points: number;
        expiresOn: string;
        note: string | null;
    }>;
}

interface MyOutDay {
    date: string;
    reason: string;
    /** Already resolved server-side — "BPAT class", "PTO", "Called in". */
    label: string;
}

interface PtoRequest {
    id: string;
    startDate: string;
    endDate: string;
    days: number;
    status: string;
    reason: string | null;
}

/** "Mon, Sep 14" — parsed at UTC noon so the date never slides a day. */
function formatOutDate(date: string): string {
    const d = new Date(`${date}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return date;
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    });
}

/**
 * Today's date on the employee's working clock (America/Chicago), as
 * YYYY-MM-DD. `new Date().toISOString()` is UTC, so from 7 PM Central onward it
 * already reads as tomorrow — a tech calling in at 9 PM would have been logged
 * off for the wrong day. The server classifies against Central
 * (services/callInClassification.ts), so the picker has to agree with it.
 */
function centralToday(): string {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Chicago",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(new Date());
}

/** Add whole days to a YYYY-MM-DD without tripping over DST. */
function addDays(date: string, days: number): string {
    const d = new Date(`${date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

/**
 * Beyond this it is not a call-in, it is a time-off request — the same ceiling
 * the server enforces (MAX_LEAD_DAYS, services/callInDayParse.ts).
 */
const MAX_CALL_IN_LEAD_DAYS = 30;

/** Server-supplied plain English if there is any, else a usable fallback. */
function apiMessage(e: unknown, fallback: string): string {
    if (e instanceof ApiError) {
        const body = e.body as { message?: string; error?: string } | undefined;
        return body?.message || body?.error || fallback;
    }
    return fallback;
}

function businessDaysBetween(start: string, end: string): number {
    if (!start || !end || end < start) return 0;
    let count = 0;
    const cur = new Date(`${start}T00:00:00Z`);
    const last = new Date(`${end}T00:00:00Z`);
    let guard = 0;
    while (cur.getTime() <= last.getTime() && guard < 400) {
        const dow = cur.getUTCDay();
        if (dow !== 0 && dow !== 6) count++;
        cur.setUTCDate(cur.getUTCDate() + 1);
        guard++;
    }
    return count;
}

/**
 * Field self-service time off page: PTO balance + request history, request
 * dialog, and the "Report absence for today" call-in (with the follow-up
 * offer to convert the unpaid day into a PTO request). Moved out of the
 * My Credentials page so that page stays focused on licenses, gauges, and
 * the saved signature.
 */
export default function FieldTimeOffPage() {
    const qc = useQueryClient();

    const { data: ptoData } = useQuery({
        queryKey: ["me-pto-balance"],
        queryFn: () => api.get<{ balance: PtoBalance }>("/api/me/pto-balance"),
    });

    // Handbook §3.6 — a person's occurrence total is theirs to see, any time.
    const { data: attendance } = useQuery({
        queryKey: ["me-attendance"],
        queryFn: () => api.get<MyAttendance>("/api/me/attendance"),
    });

    // #1268 — the days the office has you off the schedule for: approved PTO,
    // a logged call-in, or a class they entered. Your own only — a tech has no
    // view of anyone else's.
    const { data: outDaysData } = useQuery({
        queryKey: ["me-out-days"],
        queryFn: () => api.get<{ days: MyOutDay[] }>("/api/me/out-days"),
    });

    const { data: ptoReqData } = useQuery({
        queryKey: ["me-pto-requests"],
        queryFn: () => api.get<{ requests: PtoRequest[] }>("/api/me/pto-requests"),
    });

    const [reqOpen, setReqOpen] = useState(false);
    const [reqStart, setReqStart] = useState("");
    const [reqEnd, setReqEnd] = useState("");
    const [reqReason, setReqReason] = useState("");

    // Absence reporting: which day, and the follow-up offer to convert that day
    // into a PTO day. `callInDay` is the day the last report actually landed on,
    // so the PTO prompt covers the day the tech chose rather than always today.
    const [callInOpen, setCallInOpen] = useState(false);
    const [callInDate, setCallInDate] = useState("");
    const [callInDay, setCallInDay] = useState("");
    const [callInPromptOpen, setCallInPromptOpen] = useState(false);

    const resetRequestForm = () => {
        setReqStart("");
        setReqEnd("");
        setReqReason("");
    };

    const todayStr = centralToday();
    const tomorrowStr = addDays(todayStr, 1);
    const maxCallInStr = addDays(todayStr, MAX_CALL_IN_LEAD_DAYS);

    // ISO dates compare correctly as strings, so this mirrors the server's
    // own range check (today .. today + MAX_LEAD_DAYS) without a second clock.
    const callInValid = !!callInDate && callInDate >= todayStr && callInDate <= maxCallInStr;

    const openCallIn = () => {
        setCallInDate(todayStr);
        setCallInOpen(true);
    };

    const reportAbsence = useMutation({
        mutationFn: (date: string) => api.post("/api/me/call-ins", { callInDate: date }),
        onSuccess: (_result, date) => {
            setCallInOpen(false);
            setCallInDay(date);
            toast.success(`Absence reported for ${formatOutDate(date)}. The office has been notified.`);
            qc.invalidateQueries({ queryKey: ["me-pto-balance"] });
            // A call-in writes an out-day and may charge an occurrence, so the
            // two lists that show them have to refetch or the tech sees a page
            // that disagrees with what he just did.
            qc.invalidateQueries({ queryKey: ["me-out-days"] });
            qc.invalidateQueries({ queryKey: ["me-attendance"] });
            // Offer to use a PTO day for it (otherwise it defaults to unpaid).
            setCallInPromptOpen(true);
        },
        onError: (e: unknown) => toast.error(`Could not report: ${apiMessage(e, "error")}`),
    });

    const submitRequest = useMutation({
        mutationFn: () =>
            api.post("/api/me/pto-requests", {
                startDate: reqStart,
                endDate: reqEnd || reqStart,
                days: businessDaysBetween(reqStart, reqEnd || reqStart),
                reason: reqReason || undefined,
            }),
        onSuccess: () => {
            toast.success("PTO request submitted.");
            setReqOpen(false);
            resetRequestForm();
            qc.invalidateQueries({ queryKey: ["me-pto-requests"] });
        },
        onError: (e: unknown) =>
            toast.error(
                `Could not submit: ${(e instanceof ApiError && (e.body as { error?: string })?.error) || "error"}`,
            ),
    });

    return (
        <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
            <Link
                to="/field"
                className="inline-flex items-center gap-1 text-sm text-shell-foreground/70 hover:text-shell-foreground"
            >
                <ArrowLeft className="h-4 w-4" /> Back
            </Link>

            {attendance && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">My attendance</CardTitle>
                        <p className="text-xs text-foreground/70">
                            An "occurrence" is a point charged for an attendance miss — a no-show, an unexcused absence,
                            or a late call-in. Points add up on a rolling year and fall off on their own.
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-3xl font-black leading-none tracking-tight tabular-nums">
                                    {attendance.balance}
                                </div>
                                <div className="mt-1 text-xs text-foreground/70">occurrences, rolling 12 months</div>
                            </div>
                            {attendance.level ? (
                                <Badge variant={attendance.level >= 3 ? "destructive" : "default"}>
                                    Level {attendance.level} — {attendance.levelAction}
                                </Badge>
                            ) : (
                                <Badge variant="secondary">Clean record</Badge>
                            )}
                        </div>

                        <div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                                <div
                                    className={`h-full rounded-full ${
                                        attendance.balance >= 5
                                            ? "bg-destructive"
                                            : attendance.balance >= 2
                                              ? "bg-amber-500"
                                              : "bg-foreground/40"
                                    }`}
                                    style={{
                                        width: `${Math.min(100, Math.round((attendance.balance / attendance.terminationAt) * 100))}%`,
                                    }}
                                />
                            </div>
                            <div className="mt-1 text-xs text-foreground/70">
                                You're at {attendance.balance} of {attendance.terminationAt} — reaching{" "}
                                {attendance.terminationAt} points means termination, so lower is better.
                            </div>
                        </div>

                        {attendance.occurrences.length > 0 && (
                            <div className="divide-y rounded-lg border">
                                {attendance.occurrences.map((o) => (
                                    <div key={o.id} className="flex items-start justify-between gap-3 p-2.5 text-sm">
                                        <div>
                                            <div>{o.kindLabel}</div>
                                            <div className="text-xs text-foreground/60">
                                                {o.occurredOn}
                                                {o.note ? ` · ${o.note}` : ""}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold tabular-nums">{o.points}</div>
                                            <div className="text-xs text-foreground/60">clears {o.expiresOn}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <p className="text-xs text-foreground/70">
                            A clean stretch of work clears your record on its own — each occurrence drops off twelve
                            months after the day it was charged.
                        </p>
                    </CardContent>
                </Card>
            )}

            <div>
                <h1 className="text-xl font-black tracking-tight text-shell-foreground">Time off</h1>
                <p className="text-sm text-shell-foreground/70">
                    Request paid time off (PTO) or report an absence — today, or a day you already know about.
                </p>
            </div>

            {(outDaysData?.days.length ?? 0) > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base">
                            <CalendarOff className="h-4 w-4" /> Days you&rsquo;re off the schedule
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <p className="text-xs text-foreground/60">
                            Dispatch won&rsquo;t put work on these days. If something here is wrong, tell the office —
                            these are set by them.
                        </p>
                        <ul className="divide-y rounded-lg border">
                            {(outDaysData?.days ?? []).map((d) => (
                                <li key={d.date} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                    <span className="font-medium">{formatOutDate(d.date)}</span>
                                    <span className="text-foreground/70">{d.label}</span>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <CalendarDays className="h-4 w-4" /> Time off
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {!ptoData?.balance || !ptoData.balance.hireDate || !ptoData.balance.eligible ? (
                        <p className="text-sm text-foreground/60">
                            No PTO balance yet — accrues after your first work anniversary.
                        </p>
                    ) : (
                        <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-sm">
                            <div>
                                <dt className="text-foreground/60">Remaining</dt>
                                <dd className="font-medium">
                                    {ptoData.balance.remainingDays} {pluralize(ptoData.balance.remainingDays, "day")}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-foreground/60">Used</dt>
                                <dd className="font-medium">
                                    {ptoData.balance.usedDays} {pluralize(ptoData.balance.usedDays, "day")}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-foreground/60">Granted</dt>
                                <dd className="font-medium">
                                    {ptoData.balance.totalDays} {pluralize(ptoData.balance.totalDays, "day")}
                                </dd>
                            </div>
                        </dl>
                    )}

                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => setReqOpen(true)}>
                            Request time off
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600 hover:text-rose-700"
                            disabled={reportAbsence.isPending}
                            onClick={openCallIn}
                        >
                            {reportAbsence.isPending ? "Reporting…" : "Report an absence"}
                        </Button>
                    </div>

                    {ptoReqData?.requests && ptoReqData.requests.length > 0 && (
                        <ul className="divide-y border-t pt-2 text-sm">
                            {ptoReqData.requests.slice(0, 6).map((r) => (
                                <li key={r.id} className="flex items-center justify-between gap-2 py-1.5">
                                    <span>
                                        {r.startDate}
                                        {r.endDate !== r.startDate ? ` – ${r.endDate}` : ""}{" "}
                                        <span className="text-foreground/60">({r.days}d)</span>
                                    </span>
                                    <Badge
                                        variant={
                                            r.status === "approved"
                                                ? "default"
                                                : r.status === "denied"
                                                  ? "destructive"
                                                  : "secondary"
                                        }
                                    >
                                        {r.status}
                                    </Badge>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>

            <Dialog open={reqOpen} onClose={() => setReqOpen(false)} title="Request time off">
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium" htmlFor="pto-start-date">
                                Start date
                            </label>
                            <Input
                                id="pto-start-date"
                                type="date"
                                value={reqStart}
                                onChange={(e) => setReqStart(e.target.value)}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium" htmlFor="pto-end-date">
                                End date
                            </label>
                            <Input
                                id="pto-end-date"
                                type="date"
                                value={reqEnd}
                                onChange={(e) => setReqEnd(e.target.value)}
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-foreground/60">
                        {reqStart
                            ? `${businessDaysBetween(reqStart, reqEnd || reqStart)} business day(s) requested.`
                            : "Pick a start date. Leave end blank for a single day."}
                    </p>
                    <div>
                        <label className="text-sm font-medium" htmlFor="pto-reason">
                            Reason (optional)
                        </label>
                        <Textarea
                            id="pto-reason"
                            value={reqReason}
                            onChange={(e) => setReqReason(e.target.value)}
                            className="mt-1"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setReqOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={
                                !reqStart ||
                                submitRequest.isPending ||
                                businessDaysBetween(reqStart, reqEnd || reqStart) < 1
                            }
                            onClick={() => submitRequest.mutate()}
                        >
                            {submitRequest.isPending ? "Submitting…" : "Submit"}
                        </Button>
                    </div>
                </div>
            </Dialog>

            <Dialog open={callInOpen} onClose={() => setCallInOpen(false)} title="Report an absence">
                <div className="space-y-4">
                    <p className="text-sm text-foreground/70">
                        Which day will you be out? Tell the office the moment you know — a day reported ahead of the
                        deadline is charged one occurrence, while waiting until the morning of costs two, and not
                        calling at all costs three.
                    </p>

                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant={callInDate === todayStr ? "default" : "outline"}
                            onClick={() => setCallInDate(todayStr)}
                        >
                            Today
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={callInDate === tomorrowStr ? "default" : "outline"}
                            onClick={() => setCallInDate(tomorrowStr)}
                        >
                            Tomorrow
                        </Button>
                    </div>

                    <div>
                        <label className="text-sm font-medium" htmlFor="call-in-date">
                            Another day
                        </label>
                        <Input
                            id="call-in-date"
                            type="date"
                            value={callInDate}
                            min={todayStr}
                            max={maxCallInStr}
                            onChange={(e) => setCallInDate(e.target.value)}
                            className="mt-1"
                        />
                        <p className="mt-1 text-xs text-foreground/60">
                            {callInValid
                                ? `You'll be marked out on ${formatOutDate(callInDate)}, and dispatch won't put work on that day.`
                                : `Pick today or any day up to ${MAX_CALL_IN_LEAD_DAYS} days out. Anything further is a time-off request, and a day already past has to be recorded by the office.`}
                        </p>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setCallInOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!callInValid || reportAbsence.isPending}
                            onClick={() => reportAbsence.mutate(callInDate)}
                        >
                            {reportAbsence.isPending ? "Reporting…" : "Report absence"}
                        </Button>
                    </div>
                </div>
            </Dialog>

            <Dialog open={callInPromptOpen} onClose={() => setCallInPromptOpen(false)} title="Use a PTO day?">
                <div className="space-y-4">
                    <p className="text-sm text-foreground/70">
                        Your absence on {callInDay ? formatOutDate(callInDay) : "that day"} is logged as unpaid by
                        default. Would you like to request a PTO day to cover it? Your manager will review the request.
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setCallInPromptOpen(false)}>
                            No, keep unpaid
                        </Button>
                        <Button
                            onClick={() => {
                                setCallInPromptOpen(false);
                                setReqStart(callInDay);
                                setReqEnd(callInDay);
                                setReqReason("Call-in / sick day");
                                setReqOpen(true);
                            }}
                        >
                            Request PTO for that day
                        </Button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
